import { useCallback, useEffect, useRef, useState } from "react";
import errorToast from "../../../helper-components/error-toast";
import { trayListApi } from "../../../../redux/apis/tray-api";
import {
  appendPage,
  errorMessage,
  failureMessage,
  nextPageToLoad,
  removeTray,
  replaceTray,
} from "./tray-list-state";

export const TRAY_PAGE_SIZE = 20;

/**
 * Infinite-scroll state for one Active / Inactive tab of the tray master.
 *
 * - reload() starts over at page 1 (tab or search change, after a create).
 * - loadMore() is safe to call on every scroll: it does nothing while a page is
 *   in flight, once every tray is loaded, or after a failed page (until retry()).
 * - retry() re-asks for the page that failed.
 * - replace() / remove() apply a save or status change locally without a refetch.
 *
 * A response that arrives after the tab or search changed is dropped by request
 * id, so a slow page from the old filter can't land in the new list.
 */
const useTrayList = ({ status, searchValue, enabled }) => {
  const [trays, setTrays] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Mirrors of state read inside async callbacks, so they see the latest values.
  const traysRef = useRef([]);
  const hasMoreRef = useRef(false);
  const loadErrorRef = useRef(false);
  const loadingRef = useRef(false);
  const requestIdRef = useRef(0);
  const lastPageRef = useRef(0);
  const lastAddedRef = useRef(0);

  const setList = useCallback((next) => {
    traysRef.current = next;
    setTrays(next);
  }, []);

  const setMore = useCallback((next) => {
    hasMoreRef.current = next;
    setHasMore(next);
  }, []);

  // A failed page stops auto-loading (onRowsRendered fires on every render, so
  // retrying on its own would hammer the server) and keeps hasMore as it was,
  // so the list can offer a Retry row in place of the loader.
  const setError = useCallback((next) => {
    loadErrorRef.current = next;
    setLoadError(next);
  }, []);

  const fetchPage = useCallback(
    (reset) => {
      if (!reset && (loadingRef.current || !hasMoreRef.current || loadErrorRef.current)) return;
      setError(false);
      if (reset) {
        requestIdRef.current += 1;
        lastPageRef.current = 0;
        lastAddedRef.current = 0;
        setList([]);
        setTotalCount(0);
        setMore(false);
      }
      const requestId = requestIdRef.current;
      const pageNumber = nextPageToLoad({
        loadedCount: traysRef.current.length,
        pageSize: TRAY_PAGE_SIZE,
        lastPage: lastPageRef.current,
        lastAdded: lastAddedRef.current,
      });
      loadingRef.current = true;
      setLoading(true);

      trayListApi({ pageNumber, pageSize: TRAY_PAGE_SIZE, searchValue, status })
        .then((res) => {
          if (requestId !== requestIdRef.current) return;
          const failed = failureMessage(res);
          if (failed) {
            errorToast(failed);
            setError(true);
            return;
          }
          const data = res?.data?.data ?? {};
          const rows = Array.isArray(data.trays) ? data.trays : [];
          const current = traysRef.current;
          const merged = appendPage(current, rows);
          const total = Number(data.totalCount ?? merged.length) || 0;
          lastPageRef.current = pageNumber;
          lastAddedRef.current = merged.length - current.length;
          setList(merged);
          setTotalCount(total);
          setMore(rows.length > 0 && merged.length < total);
        })
        .catch((err) => {
          if (requestId !== requestIdRef.current) return;
          errorToast(errorMessage(err, "Failed to load trays"));
          setError(true);
        })
        .finally(() => {
          if (requestId !== requestIdRef.current) return;
          loadingRef.current = false;
          setLoading(false);
        });
    },
    [searchValue, status, setList, setMore, setError]
  );

  useEffect(() => {
    if (enabled) fetchPage(true);
  }, [enabled, fetchPage]);

  const loadMore = useCallback(() => fetchPage(false), [fetchPage]);
  const reload = useCallback(() => fetchPage(true), [fetchPage]);

  // Page 1 failing leaves nothing to append to, so that retry starts over.
  const retry = useCallback(() => {
    loadErrorRef.current = false;
    fetchPage(traysRef.current.length === 0);
  }, [fetchPage]);

  const replace = useCallback(
    (saved) => setList(replaceTray(traysRef.current, saved)),
    [setList]
  );

  // A status change can land after the user switched tab or search; only count
  // it when the tray is actually in the list being shown.
  const remove = useCallback(
    (trayId) => {
      const next = removeTray(traysRef.current, trayId);
      if (next.length === traysRef.current.length) return;
      setList(next);
      setTotalCount((count) => Math.max(0, count - 1));
    },
    [setList]
  );

  return {
    trays,
    totalCount,
    loading,
    hasMore,
    loadError,
    loadMore,
    reload,
    retry,
    replace,
    remove,
  };
};

export default useTrayList;
